import { useEffect, useState } from 'react';
import { useV2 } from './ctx';
import { Sheet, Field, MoneyInput, Effect } from './ui';
import { amtOf, sgnOf, rp, rpS, dayOf, shortDate, todayIso, dayName } from './model';

const wantsTruck = (legacy, tipe) => legacy.RITASE_TIPE.includes(tipe);

// ── Catat bayar / terima ────────────────────────────────────
// Same as plan: saved immediately. Different from plan: goes to the
// Director (server tiers it as an exception), shown as "menunggu".
export function CatatBayarSheet({ line, onClose, presetAmount, presetDate }) {
  const { api, user, model, monthKey, planId, legacy, done } = useV2();
  const planned = amtOf(line);
  const isIn = !(line.pengeluaran > 0);
  // presetAmount/presetDate: "Catat sesuai bank" (from Accurate's entry)
  const [amount, setAmount] = useState(String(presetAmount || planned));
  const [date, setDate] = useState(presetDate || todayIso());
  const [noTruk, setNoTruk] = useState(line.no_truk || '');
  const [ritMonth, setRitMonth] = useState(monthKey);
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trips, setTrips] = useState(null);
  const [tripAt, setTripAt] = useState('');
  const n = parseInt(amount || '0', 10);
  const same = n === planned;
  const truck = wantsTruck(legacy, line.tipe);
  const digits4 = noTruk.replace(/\D/g, '');
  useEffect(() => {
    if (!truck || digits4.length < 4) { setTrips(null); return; }
    let live = true;
    api.tripsFor(digits4, line.tipe).then(d => { if (!live) return; const list = d.trips || []; setTrips(list); const unpaid = list.filter(x => !x.paid); setTripAt(unpaid.length ? unpaid[unpaid.length - 1].at : ''); }).catch(() => live && setTrips([]));
    return () => { live = false; };
  }, [truck, digits4, line.tipe, api]);
  const picked = (trips || []).find(x => x.at === tripAt);
  const submit = async (confirmDuplicate) => {
    setSaving(true);
    try {
      const r = await api.createRequest({
        monthKey, planId, planLineId: line.id, keterangan: line.keterangan, tipe: line.tipe, customer: line.customer,
        noTruk, amount: n, requestedBy: user.name, requestDate: date,
        ritaseMonthKey: ritMonth !== monthKey ? ritMonth : undefined,
        tripAt: picked ? picked.at : undefined, tripLabel: picked ? picked.label : undefined, confirmDuplicate,
      });
      done(r.autoApproved ? `${isIn ? 'Diterima' : 'Dibayar'}: ${line.keterangan}` : (r.message || 'Menunggu persetujuan Direktur'));
    } catch (e) {
      alert(e.message); // a line already paid can't be paid again (server, 27 Oct)
    }
    setSaving(false);
  };
  return (
    <Sheet title={isIn ? 'Catat terima' : 'Catat bayar'} onClose={onClose}>
      <div className="fld">
        <Field label="Baris"><span className="t" style={{ textAlign: 'right' }}>{line.keterangan}</span></Field>
        <Field label={isIn ? 'Tanggal terima' : 'Tanggal bayar'}><input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
        {truck && <Field label="Truk"><input value={noTruk} onChange={e => setNoTruk(e.target.value)} /></Field>}
        {truck && trips !== null && (
          <Field label="Trip GPS">
            <select value={tripAt} onChange={e => setTripAt(e.target.value)}>
              {trips.map(x => <option key={x.at} value={x.at}>{x.label}{x.paid ? ' · sudah dibayar' : ''}</option>)}
              <option value="">Belum berangkat / tidak ada</option>
            </select>
          </Field>
        )}
        {truck && (
          <Field label="Rit untuk bulan">
            <select value={ritMonth} onChange={e => setRitMonth(e.target.value)}>
              {[0, 1, 2].map(k => { const m = legacy.monthKeyMinus(monthKey, k); return <option key={m} value={m}>{legacy.toMonthLabel(m)}</option>; })}
            </select>
          </Field>
        )}
      </div>
      <div className="fld"><Field label={isIn ? 'Jumlah diterima' : 'Jumlah ditransfer'}><MoneyInput value={amount} onChange={setAmount} autoFocus /></Field></div>
      {picked?.paid && <div className="note warn">Trip ini sudah ada pembayarannya. Pastikan bukan pembayaran dobel.</div>}
      {same ? (
        <>
          <div className="check" onClick={() => setOk(!ok)} role="checkbox" aria-checked={ok} tabIndex={0} onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && setOk(!ok)}>
            <span className={`cb ${ok ? 'on' : ''}`}>{ok ? '✓' : ''}</span>
            <span>Jumlah ini sama dengan bukti transfer, bukan hanya angka plan.</span>
          </div>
          <div className="note ok"><b>Sama dengan plan.</b> Langsung tersimpan, tidak perlu persetujuan.</div>
        </>
      ) : (
        <div className="note warn"><b>Beda {rpS((n - planned))} dari plan ({rp(planned)}).</b> Menunggu persetujuan Direktur; tampil sebagai menunggu sampai diputuskan.</div>
      )}
      <Effect label={same ? 'Saldo akhir bulan' : 'Saldo akhir bulan kalau disetujui'} from={model.settledEnd} to={model.settledEnd + sgnOf(line) * (n - planned)} />
      <button className="btn" disabled={saving || !n || (same && !ok) || (truck && !noTruk.trim())} onClick={() => submit()}>{saving ? 'Menyimpan…' : same ? (isIn ? 'Catat terima' : 'Catat bayar') : 'Kirim ke Direktur'}</button>
    </Sheet>
  );
}

// ── Ubah (jumlah / tanggal / keterangan / batalkan) ──────────
// Every edit is an amendment: applied at once and shown hatched until the
// Director decides. `mode` can be preset (e.g. 'tanggal' after a drag).
export function UbahSheet({ line, mode: initMode = 'jumlah', presetDate, presetAmount, onClose }) {
  const { api, user, model, monthKey, planId, done } = useV2();
  const [mode, setMode] = useState(initMode);
  const [amount, setAmount] = useState(presetAmount ? String(presetAmount) : ''); // presetAmount: "Ubah ke jumlah faktur"
  const [date, setDate] = useState(presetDate || dayOf(line.tanggal));
  const [ket, setKet] = useState(line.keterangan);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const cur = amtOf(line), s = sgnOf(line);
  const n = parseInt(amount || '0', 10);
  let effTo = model.settledEnd, valid = !!reason.trim(), effNote = null;
  if (mode === 'jumlah') { effTo = amount ? model.settledEnd + s * (n - cur) : model.settledEnd; valid = valid && n > 0 && n !== cur; }
  if (mode === 'tanggal') { valid = valid && date && date !== dayOf(line.tanggal) && date.slice(0, 7) === monthKey; effNote = date && date.slice(0, 7) !== monthKey ? 'Tanggal di luar bulan ini belum bisa: pindah ke bulan lain menyusul.' : `Hanya hari yang berubah: ${shortDate(line.tanggal)} → ${shortDate(date)}.`; }
  if (mode === 'keterangan') { valid = valid && ket.trim() && ket.trim() !== line.keterangan; }
  if (mode === 'batal') { effTo = model.settledEnd - s * cur; }
  const submit = async () => {
    setSaving(true);
    const base = { planId, planLineId: line.id, monthKey, reason, requestedBy: user.name };
    try {
      if (mode === 'jumlah') await api.createAmendment({ ...base, amendmentType: 'amount_change', originalValue: `Rp ${cur.toLocaleString('id-ID')}`, requestedValue: `Rp ${n.toLocaleString('id-ID')}` });
      if (mode === 'tanggal') await api.createAmendment({ ...base, amendmentType: 'date_change', originalValue: dayOf(line.tanggal), requestedValue: date });
      if (mode === 'keterangan') await api.createAmendment({ ...base, amendmentType: 'description_change', originalValue: line.keterangan, requestedValue: ket.trim() });
      if (mode === 'batal') await api.createAmendment({ ...base, amendmentType: 'cancel_line', originalValue: `${line.keterangan} · ${line.pengeluaran > 0 ? 'Pengeluaran' : 'Penerimaan'} Rp ${cur.toLocaleString('id-ID')}`, requestedValue: 'CANCEL' });
      done('Tersimpan · menunggu persetujuan Direktur');
    } catch (e) { alert(e.message); }
    setSaving(false);
  };
  const titles = { jumlah: 'Ubah jumlah', tanggal: 'Pindah tanggal', keterangan: 'Ubah keterangan', batal: 'Batalkan baris' };
  return (
    <Sheet title={titles[mode]} onClose={onClose}>
      <div className="seg" style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)' }}>
        {Object.entries({ jumlah: 'Jumlah', tanggal: 'Tanggal', keterangan: 'Keterangan', batal: 'Batalkan' }).map(([k, l]) => <button key={k} type="button" className={mode === k ? 'on' : ''} onClick={() => setMode(k)}>{l}</button>)}
      </div>
      <div className="fld">
        <Field label="Baris"><span className="t" style={{ textAlign: 'right' }}>{line.keterangan}</span></Field>
        {mode === 'jumlah' && <Field label="Jumlah sekarang"><span className="amt">{rp(cur)}</span></Field>}
        {mode === 'tanggal' && <Field label="Dari"><span>{dayName(dayOf(line.tanggal))}</span></Field>}
      </div>
      <div className="fld">
        {mode === 'jumlah' && <Field label="Jumlah baru"><MoneyInput value={amount} onChange={setAmount} autoFocus /></Field>}
        {mode === 'tanggal' && <Field label="Ke"><input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>}
        {mode === 'keterangan' && <Field label="Baru"><input value={ket} onChange={e => setKet(e.target.value)} /></Field>}
        {mode === 'batal' && <Field label="Dibatalkan"><span className="amt bad">{rpS(-s * cur)} dari saldo</span></Field>}
        <Field label="Alasan"><input placeholder="wajib" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      </div>
      <Effect label="Saldo akhir bulan kalau disetujui" from={model.settledEnd} to={effTo} note={effNote} />
      <div className="note info">Langsung tampil diarsir dengan <span className="pill p-pend">Menunggu</span>. Kalau ditolak, kembali seperti semula.</div>
      <button className="btn" style={mode === 'batal' ? { background: 'var(--red)' } : undefined} disabled={saving || !valid} onClick={submit}>{saving ? 'Menyimpan…' : 'Simpan'}</button>
    </Sheet>
  );
}

// ── Tambah baris ────────────────────────────────────────────
// Money direction first, so tipe only offers the right kind (#48).
// preset: a guess to start from (bank-only row → guessFromBank), every
// field still editable. preset.monthKey/planId: add to another month's plan
// (Perbaiki bulan lalu).
export function TambahSheet({ presetDate, presetType = 'add_disbursement', preset, onClose }) {
  const ctx = useV2();
  const { api, user, model, legacy, done } = ctx;
  const monthKey = preset?.monthKey || ctx.monthKey, planId = preset?.planId || ctx.planId;
  const [type, setType] = useState(preset?.type || presetType);
  const [customer, setCustomer] = useState(preset?.customer || '');
  const [tipe, setTipe] = useState(preset?.tipe || '');
  const [noTruk, setNoTruk] = useState(preset?.truk || '');
  const [date, setDate] = useState(preset?.date || presetDate || todayIso());
  const [ket, setKet] = useState(preset?.ket || '');
  const [amount, setAmount] = useState(preset?.amount || '');
  const [reason, setReason] = useState(preset?.reason || '');
  const [rekening, setRekening] = useState(preset?.rekening || 'BCA'); // OCBC = parked money beside BCA (30 Oct)
  const [saving, setSaving] = useState(false);
  const opts = customer ? legacy.addTipeOptionsFor(customer, type) : [];
  // keep a preset tipe when it's valid for the customer; otherwise the first option
  useEffect(() => { setTipe(t => (opts.includes(t) ? t : opts[0] || '')); }, [customer, type]);
  const n = parseInt(amount || '0', 10);
  const inflow = type === 'add_inflow';
  const truck = tipe && wantsTruck(legacy, tipe);
  const valid = customer && tipe && ket.trim() && n > 0 && date && date.slice(0, 7) === monthKey && reason.trim() && (!truck || noTruk.trim());
  const submit = async () => {
    setSaving(true);
    try {
      await api.createAmendment({
        planId, planLineId: null, monthKey, amendmentType: type, originalValue: null, reason, requestedBy: user.name,
        requestedValue: JSON.stringify({ keterangan: ket.trim(), tipe, customer, no_truk: noTruk, tanggal: date, pengeluaran: inflow ? 0 : n, penerimaan: inflow ? n : 0, rekening }),
      });
      await done('Tersimpan · menunggu persetujuan Direktur');
      if (preset?.returnTo) ctx.open(preset.returnTo); // back to the list it came from
    } catch (e) { alert(e.message); }
    setSaving(false);
  };
  return (
    <Sheet title={preset?.monthKey && preset.monthKey !== ctx.monthKey ? `Tambah baris · ${legacy.toMonthLabel(preset.monthKey)}` : 'Tambah baris'} onClose={onClose}>
      {preset && <div className="note" style={{ background: 'var(--amber-dim)', fontSize: 12.5 }}>Diisi otomatis dari {preset.from || 'bank'} (Accurate). Cek customer, tipe dan keterangan sebelum simpan.</div>}
      <div className="seg" style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <button type="button" className={!inflow ? 'on' : ''} onClick={() => setType('add_disbursement')}>Uang keluar</button>
        <button type="button" className={inflow ? 'on' : ''} onClick={() => setType('add_inflow')}>Uang masuk</button>
      </div>
      <div className="fld">
        <Field label="Customer"><select value={customer} onChange={e => setCustomer(e.target.value)}><option value="">Pilih…</option>{legacy.CUSTOMER_SELECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field>
        <Field label="Tipe"><select value={tipe} onChange={e => setTipe(e.target.value)} disabled={!customer}>{opts.length ? opts.map(t => <option key={t} value={t}>{legacy.TIPE_LABELS[t] || t}</option>) : <option value="">{customer ? 'Tidak ada tipe ' + (inflow ? 'uang masuk' : 'uang keluar') : '—'}</option>}</select></Field>
        {truck && <Field label="Truk"><input placeholder="wajib" value={noTruk} onChange={e => setNoTruk(e.target.value)} /></Field>}
        <Field label="Tanggal"><input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
        <Field label="Rekening"><select value={rekening} onChange={e => setRekening(e.target.value)}><option value="BCA">BCA</option><option value="OCBC">OCBC (PT AJT)</option></select></Field>
        <Field label="Keterangan"><input value={ket} onChange={e => setKet(e.target.value)} /></Field>
      </div>
      <div className="fld"><Field label="Jumlah"><MoneyInput value={amount} onChange={setAmount} /></Field></div>
      <div className="fld"><Field label="Alasan"><input placeholder="wajib" value={reason} onChange={e => setReason(e.target.value)} /></Field></div>
      <div className={`note ${inflow ? 'in' : 'out'}`}>Uang {inflow ? 'MASUK +' : 'KELUAR −'}Rp {n.toLocaleString('id-ID')}</div>
      {monthKey === ctx.monthKey && (rekening === 'OCBC'
        ? <Effect label="Saldo OCBC akhir bulan kalau disetujui" from={model.ocbc.planEnd} to={model.ocbc.planEnd + (inflow ? n : -n)} note="Saldo BCA tidak berubah" />
        : <Effect label="Saldo akhir bulan kalau disetujui" from={model.settledEnd} to={model.settledEnd + (inflow ? n : -n)} />)}
      <button className="btn" disabled={saving || !valid} onClick={submit}>{saving ? 'Menyimpan…' : 'Simpan · diarsir sampai disetujui'}</button>
    </Sheet>
  );
}

// ── Ganti truk (immediate) ──────────────────────────────────
export function GantiTrukSheet({ line, onClose }) {
  const { api, monthKey, done, model } = useV2();
  const [noTruk, setNoTruk] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // A paid trip line goes through the old fix-flag endpoint first: it also
  // creates the ritase row a truck-less payment never got (ops_note) and
  // clears the flag, so the nightly GPS match can pair it.
  const paid = line.status === 'disbursed' ? model.paidReq[line.id] : null;
  const submit = async () => {
    setSaving(true);
    try {
      if (paid) await api.fixFlaggedRequest(paid.id, noTruk.trim());
      await api.changeTruck(line.id, noTruk.trim(), monthKey, reason);
      await done(`Truk diganti: ${line.no_truk || '—'} → ${noTruk.trim()}`);
    }
    catch (e) { alert(e.message); }
    setSaving(false);
  };
  return (
    <Sheet title="Ganti truk" onClose={onClose}>
      <div className="fld">
        <Field label="Baris"><span className="t" style={{ textAlign: 'right' }}>{line.keterangan}</span></Field>
        <Field label="Truk sekarang"><span>{line.no_truk || '—'}</span></Field>
      </div>
      <div className="fld">
        <Field label="Truk baru"><input autoFocus inputMode="numeric" value={noTruk} onChange={e => setNoTruk(e.target.value)} /></Field>
        <Field label="Alasan"><input placeholder="opsional" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      </div>
      <div className="note ok"><b>Langsung tersimpan.</b> Tidak mengubah uang. Rit ikut pindah ke truk baru; tetap tercatat di riwayat.</div>
      <button className="btn" disabled={saving || !/^\d{3,5}$/.test(noTruk.replace(/\D/g, ''))} onClick={submit}>{saving ? 'Menyimpan…' : 'Simpan'}</button>
    </Sheet>
  );
}

// ── Koreksi (paid lines) ────────────────────────────────────
export function KoreksiSheet({ line, onClose }) {
  const { api, user, role, model, monthKey, done, data } = useV2();
  const req = (data.requests?.items || []).find(r => r.plan_line_id === line.id && r.status === 'approved');
  const [mode, setMode] = useState('jumlah');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const paid = model.paidOr(line, amtOf(line));
  const n = parseInt(amount || '0', 10);
  const submit = async () => {
    setSaving(true);
    try {
      if (mode === 'jumlah') { await api.reopenRequest(req.id, n, reason); done('Koreksi jumlah dikirim · menunggu Direktur'); }
      else if (role === 'director') { await api.reversePayment({ planLineId: line.id, monthKey, reason, reversedBy: user.name }); done('Pembayaran dibatalkan'); }
      else { await api.requestReverse(req.id, reason); done('Pembatalan dikirim · menunggu Direktur'); }
    } catch (e) { alert(e.message); }
    setSaving(false);
  };
  if (!req && mode === 'jumlah') return <Sheet title="Koreksi" onClose={onClose}><div className="note info">Pembayaran baris ini tidak ditemukan.</div></Sheet>;
  return (
    <Sheet title="Koreksi pembayaran" onClose={onClose}>
      <div className="seg" style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <button type="button" className={mode === 'jumlah' ? 'on' : ''} onClick={() => setMode('jumlah')}>Koreksi jumlah</button>
        <button type="button" className={mode === 'batal' ? 'on' : ''} onClick={() => setMode('batal')}>Batalkan pembayaran</button>
      </div>
      <div className="fld">
        <Field label="Baris"><span className="t" style={{ textAlign: 'right' }}>{line.keterangan}</span></Field>
        <Field label="Dibayar"><span className="amt">{rp(paid)}</span></Field>
      </div>
      <div className="fld">
        {mode === 'jumlah' && <Field label="Jumlah benar"><MoneyInput value={amount} onChange={setAmount} autoFocus /></Field>}
        <Field label="Alasan"><input placeholder="wajib" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      </div>
      {mode === 'jumlah'
        ? <Effect label="Saldo akhir bulan kalau disetujui" from={model.settledEnd} to={model.settledEnd + sgnOf(line) * (n - paid)} />
        : <div className="note info">Baris kembali ke "belum dibayar"; saldo tidak berubah karena baris tetap ada di plan.{role === 'director' ? ' Sebagai Direktur, ini langsung berlaku.' : ' Menunggu persetujuan Direktur.'}</div>}
      <button className="btn" style={mode === 'batal' ? { background: 'var(--red)' } : undefined} disabled={saving || !reason.trim() || (mode === 'jumlah' && (!n || n === paid))} onClick={submit}>{saving ? 'Menyimpan…' : 'Kirim'}</button>
    </Sheet>
  );
}
